import * as React from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Create a poll message. Options are lightweight (label only); the
 * server hydrates ids client-side is fine — we send string ids too.
 */
export default function PollComposerDialog({ open, onOpenChange, companyId, conversation }) {
  const [question, setQuestion] = React.useState("");
  const [options, setOptions] = React.useState(["", ""]);
  const [allowMultiple, setAllowMultiple] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) { setQuestion(""); setOptions(["", ""]); setAllowMultiple(false); }
  }, [open]);

  function setOpt(i, v) { setOptions((prev) => prev.map((o, idx) => idx === i ? v : o)); }
  function addOpt() { setOptions((prev) => prev.length < 8 ? [...prev, ""] : prev); }
  function removeOpt(i) {
    setOptions((prev) => prev.length > 2 ? prev.filter((_, idx) => idx !== i) : prev);
  }

  async function create() {
    const q = question.trim();
    const opts = options.map((o, i) => ({ id: `o${i}`, label: (o || "").trim() }))
      .filter((o) => o.label);
    if (!q || opts.length < 2) {
      toast.error("Give the poll a question and at least two options.");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/companies/${companyId}/comms/conversations/${conversation.id}/polls`,
        { question: q, options: opts, allow_multiple: allowMultiple },
      );
      onOpenChange(false);
      toast.success("Poll posted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't post poll");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="poll-composer-dialog">
        <DialogHeader>
          <DialogTitle>Create a poll</DialogTitle>
          <DialogDescription>
            Members vote once (unless you allow multiple). Live tallies update
            for everyone.
          </DialogDescription>
        </DialogHeader>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Poll question"
          className="field-input h-9 text-[13px]"
          maxLength={280}
          data-testid="poll-composer-question"
        />
        <div className="mt-2 space-y-1.5">
          {options.map((o, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              <input
                type="text"
                value={o}
                onChange={(e) => setOpt(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
                className="field-input h-8 text-[12.5px] flex-1"
                maxLength={120}
                data-testid={`poll-composer-option-${i}`}
              />
              {options.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeOpt(i)}
                  className="btn-ghost h-8 w-8 !p-0"
                  aria-label="Remove option"
                >
                  <X size={11} />
                </button>
              )}
            </div>
          ))}
          {options.length < 8 && (
            <button
              type="button"
              onClick={addOpt}
              className="btn-ghost h-8 text-[11.5px]"
              data-testid="poll-composer-add-option"
            >
              <Plus size={11} /> Add option
            </button>
          )}
        </div>
        <label className="mt-2 flex items-center gap-2 text-[12px] text-brand-muted">
          <input
            type="checkbox"
            checked={allowMultiple}
            onChange={(e) => setAllowMultiple(e.target.checked)}
            data-testid="poll-composer-multi"
          />
          Allow multiple choices
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost h-8" onClick={() => onOpenChange(false)}>Cancel</button>
          <button
            type="button"
            onClick={create}
            disabled={busy}
            className="btn-violet h-8"
            data-testid="poll-composer-submit"
          >
            {busy ? "…" : "Post poll"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
